/**
 * The photo processing pipeline (M5 / S05-02).
 *
 * Takes bytes that just arrived from a camera or a gallery and produces either
 * a `VerifiedPhoto` — the ONLY thing S05-03 will be able to upload — or a typed
 * refusal. There is no third outcome and no partial success.
 *
 * THE BOUNDARY IS THE TYPE SYSTEM, NOT DISCIPLINE. `VerifiedPhoto`'s brand is a
 * module-private `unique symbol`, so it cannot be written by hand, and the only
 * function that returns one is `processPhoto` — which returns it only after the
 * independent verifier has accepted every one of the three outputs. Handing the
 * original bytes to upload code is therefore a COMPILE ERROR, not a review
 * catch. **The original is never part of the result.**
 *
 * THE ORDER IS I-040'S, AND IT IS THE POINT:
 *
 *   1. inspect the ORIGINAL in memory        → orientation, dimensions, times
 *   2. derivatives FROM THE ORIGINAL, upright, using that captured orientation
 *   3. verify each derivative independently
 *   4. sanitize the archive from the ORIGINAL bytes, separately
 *   5. verify the archive independently
 *   6. hash the STORED archive bytes
 *   7. the original goes out of scope — never persisted, never transmitted
 *
 * Step 2 must come from the ORIGINAL. Sanitization removes the EXIF block and
 * the Orientation tag goes with it, so a derivative built from the sanitized
 * archive has nothing left to tell it which way is up — a rotated photo could
 * only ever come out sideways, with no error anywhere. That trap was live in
 * the Probe C harness. The value is carried forward as DATA.
 *
 * No network. No storage. No Supabase. No secrets.
 */

import {
  inspectPhoto,
  pictureBytesPreserved,
  sanitizePhoto,
  sha256Hex,
  sniffFormat,
  verifyPhotoClean,
  type PhotoFormat,
  type PhotoInspection,
} from './photo-bytes';
import {
  fitWithin,
  orientationSwapsAxes,
  type EncodeMime,
  type ImageRasterPort,
} from '../../platform/image-raster';

/** D-026: the per-photo source ceiling, matching the hosted bucket's own limit. */
export const MAX_SOURCE_BYTES = 26_214_400; // 25 × 1024 × 1024

export const DISPLAY_LONG_EDGE = 2048;
export const THUMBNAIL_LONG_EDGE = 400;
const DERIVATIVE_QUALITY = 0.85;

/** Identifies which sanitizer build produced a row (migration 0006, PT §9). */
export const SANITIZER_VERSION = 'crewlink-photo-1';

const SUPPORTED: readonly PhotoFormat[] = ['jpeg', 'png'];

const MIME_FOR: Record<'jpeg' | 'png', EncodeMime> = {
  jpeg: 'image/jpeg',
  png: 'image/png',
};

declare const VERIFIED_PHOTO: unique symbol;

export interface VerifiedVariant {
  readonly bytes: Uint8Array;
  readonly byteSize: number;
  readonly width: number;
  readonly height: number;
  readonly mime: EncodeMime;
}

export interface VerifiedArchive extends VerifiedVariant {
  /** 64 lowercase hex characters, over the STORED sanitized bytes (PT §9). */
  readonly sha256: string;
}

/** The approved metadata subset. Nothing else from the source survives. */
export interface ApprovedMetadata {
  /** 1–8, or null. The archive no longer carries it — this is the record (I-040). */
  readonly orientation: number | null;
  /** Device-reported, verbatim as EXIF gave it, never rewritten. */
  readonly capturedAtRaw: string | null;
  /** Minutes, as migration 0006 stores it. Null when absent or unparseable. */
  readonly utcOffsetMinutes: number | null;
  readonly sourceMime: EncodeMime;
  readonly sourceByteSize: number;
}

export interface VerifiedPhoto {
  readonly [VERIFIED_PHOTO]: true;
  readonly archive: VerifiedArchive;
  readonly display: VerifiedVariant;
  readonly thumbnail: VerifiedVariant;
  readonly metadata: ApprovedMetadata;
  readonly sanitizerVersion: string;
  /** What the SOURCE contained, for reporting. Carries no location values. */
  readonly sourceInspection: PhotoInspection;
}

export type PhotoRefusalCode =
  | 'unsupported-format'
  | 'mime-mismatch'
  | 'too-large'
  | 'unreadable'
  | 'sanitize-failed'
  | 'verify-failed'
  | 'derivative-failed'
  | 'picture-altered';

export interface PhotoRefusal {
  readonly ok: false;
  readonly code: PhotoRefusalCode;
  /** Safe to show and to log. Never contains a coordinate or a byte of content. */
  readonly reason: string;
  readonly problems: readonly string[];
}

export type PhotoResult = { readonly ok: true; readonly photo: VerifiedPhoto } | PhotoRefusal;

const refuse = (
  code: PhotoRefusalCode,
  reason: string,
  problems: readonly string[] = [],
): PhotoRefusal => ({ ok: false, code, reason, problems });

/**
 * `+05:30` / `-08:00` → minutes. Returns null on anything else rather than
 * guessing — migration 0006 constrains this to ±1080 and a wrong number is
 * worse than an absent one.
 */
export function parseUtcOffsetMinutes(raw: string | null): number | null {
  if (raw === null) return null;
  const match = /^([+-])(\d{2}):(\d{2})$/.exec(raw.trim());
  if (!match) return null;
  const hours = Number(match[2]);
  const minutes = Number(match[3]);
  if (hours > 18 || minutes > 59) return null;
  const total = hours * 60 + minutes;
  if (total > 1080) return null;
  return match[1] === '-' ? -total : total;
}

/**
 * Process one photo. The only door to a `VerifiedPhoto`.
 *
 * `declaredMime` is what the picker CLAIMED. It is checked against the sniffed
 * bytes and then never trusted again.
 */
export async function processPhoto(
  bytes: Uint8Array,
  declaredMime: string,
  raster: ImageRasterPort,
): Promise<PhotoResult> {
  // ---- 0. Boundaries, before any work -------------------------------------
  if (bytes.length > MAX_SOURCE_BYTES) {
    return refuse(
      'too-large',
      `This photo is larger than the ${String(Math.round(MAX_SOURCE_BYTES / 1_048_576))} MB limit.`,
    );
  }

  const format = sniffFormat(bytes);
  if (!SUPPORTED.includes(format)) {
    return refuse(
      'unsupported-format',
      format === 'heic'
        ? 'HEIC photos are not supported yet.'
        : 'That file is not a supported photo format.',
      [`sniffed: ${format}`],
    );
  }
  const sourceFormat = format as 'jpeg' | 'png';
  const sourceMime = MIME_FOR[sourceFormat];

  // A declared type that disagrees with the bytes is a spoof or a broken
  // picker. Either way the bytes win, and the mismatch is refused rather than
  // silently resolved — a file claiming to be a PNG while being something else
  // is exactly the input worth being suspicious of.
  const declared = declaredMime.trim().toLowerCase();
  if (declared.length > 0 && declared !== sourceMime) {
    return refuse('mime-mismatch', 'That file’s contents do not match its type.', [
      `declared: ${declared}`,
      `actual: ${sourceMime}`,
    ]);
  }

  // ---- 1. Inspect the ORIGINAL, in memory ---------------------------------
  const inspection = inspectPhoto(bytes);
  if (inspection.error !== null) {
    return refuse('unreadable', 'That photo could not be read.', [inspection.error]);
  }

  const metadata: ApprovedMetadata = {
    orientation: inspection.orientation,
    capturedAtRaw: inspection.capturedAtRaw,
    utcOffsetMinutes: parseUtcOffsetMinutes(inspection.utcOffsetRaw),
    sourceMime,
    sourceByteSize: bytes.length,
  };

  // ---- 2 & 3. Derivatives FROM THE ORIGINAL, then verified -----------------
  const buildVariant = async (longEdge: number): Promise<VerifiedVariant | PhotoRefusal> => {
    let rendered;
    try {
      rendered = await raster.render({
        bytes,
        sourceMime,
        orientation: inspection.orientation,
        longEdge,
        encodeMime: sourceMime,
        quality: DERIVATIVE_QUALITY,
        // THE FILE'S OWN HEADER DIMENSIONS, read from bytes by our inspector
        // and independent of any decoder. They let the raster tell, for THIS
        // image, whether the decoder already applied the orientation — the
        // measurement that closes I-040's double-rotation (see image-raster).
        storedWidth: inspection.width ?? undefined,
        storedHeight: inspection.height ?? undefined,
      });
    } catch (error) {
      return refuse('derivative-failed', 'That photo could not be prepared for display.', [
        error instanceof Error ? error.message : String(error),
      ]);
    }

    // PT §9 requires derivatives to be METADATA-FREE, not merely
    // privacy-clean. The canvas encoder writes its own APP0/JFIF header and an
    // APP2 ICC profile — both benign, and correctly identified as such by the
    // inspector rather than mistaken for private data — but "metadata-free"
    // is literal, so they are stripped like anything else and the verifier
    // then proves zero segments remain. Removing the ICC profile is
    // pixel-identical only because the canvas colour space is pinned to sRGB
    // (I-039), which is why that pin exists.
    let clean: Uint8Array;
    try {
      clean = sanitizePhoto(rendered.bytes, sourceFormat);
    } catch (error) {
      return refuse('derivative-failed', 'That photo could not be prepared for display.', [
        error instanceof Error ? error.message : String(error),
      ]);
    }

    const problems = verifyPhotoClean(clean, sourceFormat);
    if (problems.length > 0) {
      return refuse(
        'verify-failed',
        'That photo could not be verified as safe to store.',
        problems,
      );
    }

    return {
      bytes: clean,
      byteSize: clean.length,
      width: rendered.width,
      height: rendered.height,
      mime: sourceMime,
    };
  };

  const display = await buildVariant(DISPLAY_LONG_EDGE);
  if ('ok' in display) return display;
  const thumbnail = await buildVariant(THUMBNAIL_LONG_EDGE);
  if ('ok' in thumbnail) return thumbnail;

  // ---- 4. Sanitize the archive, SEPARATELY, from the ORIGINAL --------------
  // The archive is NOT rotated. PT §9 keeps source orientation as a Crewlink
  // FIELD, and the archive is the retained full-resolution source — rotating
  // it would re-encode the picture and destroy exactly the detail it exists to
  // preserve. Anything that renders it must apply `metadata.orientation`.
  let archiveBytes: Uint8Array;
  try {
    archiveBytes = sanitizePhoto(bytes, sourceFormat);
  } catch (error) {
    return refuse('sanitize-failed', 'That photo could not be cleaned safely.', [
      error instanceof Error ? error.message : String(error),
    ]);
  }

  // ---- 5. Verify the archive independently --------------------------------
  const archiveProblems = verifyPhotoClean(archiveBytes, sourceFormat);
  if (archiveProblems.length > 0) {
    return refuse(
      'verify-failed',
      'That photo could not be verified as safe to store.',
      archiveProblems,
    );
  }

  // The picture itself must be untouched — sanitization removes metadata, not
  // detail. If this ever fails the archive is a re-compression, and PT §9's
  // retention promise would be false.
  if (!pictureBytesPreserved(bytes, archiveBytes)) {
    return refuse('picture-altered', 'That photo could not be cleaned without altering it.');
  }

  // ---- 6. Hash the STORED bytes -------------------------------------------
  const sha256 = await sha256Hex(archiveBytes);

  // Archive dimensions are the file's own, NOT the upright ones: the archive
  // keeps the sensor's orientation and carries the tag's value as data.
  const archiveWidth = inspection.width ?? 0;
  const archiveHeight = inspection.height ?? 0;
  if (archiveWidth <= 0 || archiveHeight <= 0) {
    return refuse('unreadable', 'That photo could not be read.', ['missing dimensions']);
  }

  // ---- 7. Only now does a VerifiedPhoto exist ------------------------------
  return {
    ok: true,
    photo: {
      archive: {
        bytes: archiveBytes,
        byteSize: archiveBytes.length,
        width: archiveWidth,
        height: archiveHeight,
        mime: sourceMime,
        sha256,
      },
      display,
      thumbnail,
      metadata,
      sanitizerVersion: SANITIZER_VERSION,
      sourceInspection: inspection,
    } as VerifiedPhoto,
  };
}

/**
 * The upright dimensions a renderer should present the ARCHIVE at, given the
 * orientation recorded alongside it. Exported because the archive is not
 * self-describing and every future reader needs this (I-040).
 */
export function uprightArchiveSize(photo: VerifiedPhoto): { width: number; height: number } {
  const { width, height } = photo.archive;
  return orientationSwapsAxes(photo.metadata.orientation)
    ? { width: height, height: width }
    : { width, height };
}

export { fitWithin };
